import { Button, Divider, Form, Input } from 'antd'
import React from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import { useEmailSignIn } from '@/contexts/AuthContext/hooks/emailSignIn'
import { usePwdRules } from '@/contexts/AuthContext/hooks/pwdRules'
import UserEntryFormContainer from '@/pages/tad.ai/sign-up/_blocks/UserEntryFormContainer'
import GoogleUserButton from '@/pages/tad.ai/_components/GoogleUserButton'
import LabelItem from '@/pages/tad.ai/sign-up/_blocks/LabelItem'
import FormItemEmail from '@/_blocks/Auth/FormItemEmail'

export interface ISignInFormValues {
  email: string
  password: string
}

export interface ISignInFormProps {
  children?: React.ReactNode
  handleClickSignUp?: () => void
  titleClassName?: string
  onSignInSuccess?: () => void
  title?: React.ReactNode
  titleAs?: React.ElementType
  className?: string
  formContainerClassName?: string
  readOnlyEmail?: string
}

const SignInForm: React.FC<ISignInFormProps> = (props) => {
  const { t } = useLingui()
  const {
    children,
    className,
    handleClickSignUp,
    titleClassName,
    onSignInSuccess,
    title = t`Sign in to Tad`,
    titleAs = 'h1',
    formContainerClassName,
    readOnlyEmail,
  } = props

  const [form] = Form.useForm<ISignInFormValues>()
  const pwdRules = usePwdRules(true)
  const { handleSignIn, loading } = useEmailSignIn(onSignInSuccess)

  const router = useRouter()

  const buttonRef = useRef<HTMLButtonElement | null>(null)
  // const buttonHovering = useHover(buttonRef)

  return (
    <div className={className}>
      <UserEntryFormContainer
        titleClassName={titleClassName}
        title={title}
        titleAs={titleAs}
        className={formContainerClassName}
      >
        <GoogleUserButton
          className={cls`
            w-full
          `}
        >
          <Trans>Sign in with Google</Trans>
        </GoogleUserButton>
        <Divider className='md:!my-8' plain>
          or
        </Divider>
        <Form<ISignInFormValues>
          initialValues={{
            email: readOnlyEmail || '',
          }}
          form={form}
          layout='vertical'
          className='w-full'
        >
          <FormItemEmail
            requiredOnly
            readOnly={!!readOnlyEmail}
            onClickEnter={async () => {
              const values = await form.validateFields()
              handleSignIn(values)
            }}
            inputClassName={cls`
         
            `}
            label={
              <LabelItem>
                <Trans>Email</Trans>
              </LabelItem>
            }
          />
          <Form.Item
            name='password'
            dependencies={['rePassword']}
            rules={pwdRules}
            required={false}
            label={
              <LabelItem>
                <Trans>Password</Trans>
              </LabelItem>
            }
          >
            <Input.Password
              className={`
                h-12
              `}
              maxLength={16}
              placeholder={t`Password`}
              onKeyDown={async (event) => {
                if (event.key === 'Enter') {
                  const values = await form.validateFields()
                  handleSignIn(values)
                }
              }}
            />
          </Form.Item>

          <div className='mb-6 flex items-center'>
            <Link
              href='/forgot-password'
              className={cls`
                text-f-primary hover:text-f-primary-hover
                text-center text-sm
              `}
            >
              {t`Forgot password?`}
            </Link>
          </div>

          <Form.Item noStyle>
            <Button
              onClick={async () => {
                const values = await form.validateFields()
                handleSignIn(values)
              }}
              block
              type='primary'
              loading={loading}
              // ghost={!buttonHovering}
              ref={buttonRef}
              className={`mb-8 h-12  text-lg font-semibold`}
            >
              {t`Sign in`}
            </Button>
          </Form.Item>
        </Form>
        {!readOnlyEmail ? (
          <p className={cls`text-f-text-secondary text-center text-sm`}>
            <Trans>
              Don&rsquo;t have an account?{' '}
              <span
                className='text-f-primary hover:text-f-primary-hover cursor-pointer hover:underline'
                onClick={() => {
                  if (handleClickSignUp) {
                    handleClickSignUp()
                  } else {
                    router.push('/sign-up')
                  }
                }}
              >
                Sign up here
              </span>
            </Trans>
          </p>
        ) : null}
      </UserEntryFormContainer>

      {children}
    </div>
  )
}

export default SignInForm
